import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Copy, Link2, Mail, ShieldAlert, Trash2, UserPlus } from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { formatDate } from "@/lib/format"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  fetchAccesos,
  fetchInvitaciones,
  fetchRoles,
  invitar,
  quitarAcceso,
  revocarInvitacion,
  type RolDisponible,
} from "@/routes/accesos/api"
import { t } from "@/lib/i18n"

/**
 * QUIEN PUEDE ENTRAR Y CON QUE ALCANCE.
 *
 * Hasta hoy dar acceso a alguien solo se podia con un script en el servidor, y
 * eso tiene una consecuencia contable que no es obvia: **el cierre exige
 * separacion de funciones** —el backend rechaza que quien preparo un periodo lo
 * firme— asi que un cliente con un solo usuario no puede firmar nunca. Es un
 * muro que aparece al final, cuando ya se cargo todo.
 *
 * TRES DECISIONES DE ESTA PANTALLA:
 *
 * 1. **Se ve quien tiene acceso, no solo a quien se invito.** El caso normal no
 *    es «anade a alguien», es «quien tiene acceso a esto» — y esa pregunta se
 *    hace justo cuando alguien deja la empresa.
 * 2. **Solo se ofrecen los roles que el backend va a aceptar.** Un desplegable
 *    con opciones imposibles ensena a desconfiar de la pantalla. Y no es una
 *    comodidad: `gerencia` no puede conceder `contador` a proposito, porque si
 *    no podria invitarse a si misma como firmante y la separacion de funciones
 *    dejaria de significar nada.
 * 3. **No se dice «invitacion enviada».** Esta instalacion no manda correos.
 *    Una invitacion que dijera «enviada» y no llegara es peor que ninguna: se
 *    espera, no llega, y nadie sabe si es el spam, el correo mal escrito o el
 *    sistema. Se entrega un enlace y se dice que hay que hacerlo llegar.
 */
export function AccesosPage() {
  const accesos = useQuery({ queryKey: ["accesos"], queryFn: fetchAccesos })
  const invitaciones = useQuery({ queryKey: ["invitaciones"], queryFn: fetchInvitaciones })
  const roles = useQuery({ queryKey: ["roles-disponibles"], queryFn: fetchRoles })

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Configuración"
        title={t("Accesos")}
        description={t("Quién puede entrar a este cliente y con qué alcance. El rol decide qué puede hacer cada persona, y sólo «contador» puede firmar un cierre.")}
      />

      {accesos.data?.aviso ? (
        <p className="flex items-start gap-1.5 rounded-panel border border-warning/40 bg-warning-soft px-3 py-2 text-2xs leading-5">
          <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
          <span>{accesos.data.aviso}</span>
        </p>
      ) : null}

      <Invitar roles={roles.data?.roles ?? []} nota={roles.data?.nota} />

      <DataState
        loading={accesos.isLoading}
        error={accesos.error}
        empty={false}
        onRetry={() => void accesos.refetch()}
        columns={[28, 18, 22, 18, 14]}
      >
        <QuienEntra />
      </DataState>

      <DataState
        loading={invitaciones.isLoading}
        error={invitaciones.error}
        empty={!invitaciones.isLoading && (invitaciones.data?.length ?? 0) === 0}
        emptyMessage={t("No hay ninguna invitación.")}
        emptyHint={t("Invita arriba a quien vaya a usar el sistema. Recuerda que hacen falta al menos dos personas para poder cerrar un período.")}
        onRetry={() => void invitaciones.refetch()}
        columns={[30, 16, 18, 20, 16]}
      >
        <Invitaciones />
      </DataState>
    </div>
  )
}

function QuienEntra() {
  const q = useQuery({ queryKey: ["accesos"], queryFn: fetchAccesos })
  const { toast } = useToast()
  const qc = useQueryClient()

  const quitar = useMutation({
    mutationFn: quitarAcceso,
    onSuccess: (r) => {
      toast({ title: `${r.email} ya no tiene acceso` })
      void qc.invalidateQueries({ queryKey: ["accesos"] })
      void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
    },
    onError: onWriteError("el acceso", "quitar"),
  })

  return (
    <WorkPanel
      title={t("Quién tiene acceso")}
      meta={<Badge variant="outline">{q.data?.accesos.length ?? 0}</Badge>}
      footnote={t("Quitar un acceso no borra nada: lo cierra con fecha. Quién tuvo acceso a la contabilidad de una empresa y hasta cuándo es exactamente lo que un auditor pregunta, y borrarlo dejaría el registro de auditoría con acciones de alguien que —mirando la base— nunca tuvo permiso.")}
    >
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[18rem]">{t("Persona")}</TableHead>
            <TableHead className="w-32">{t("Rol")}</TableHead>
            <TableHead className="w-[14rem]">{t("Alcance")}</TableHead>
            <TableHead className="w-28">{t("Desde")}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {(q.data?.accesos ?? []).map((a) => (
            <TableRow key={a.id}>
              <TableCell className="truncate" title={a.email}>
                <span className="font-medium">{a.nombre || a.email}</span>
                {a.nombre ? (
                  <span className="block text-2xs text-muted-foreground">{a.email}</span>
                ) : null}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="code">
                  {a.rol}
                </Badge>
              </TableCell>
              <TableCell className="text-2xs text-muted-foreground">
                {a.razon_social ? `Sólo ${a.razon_social}` : "Todo el cliente"}
              </TableCell>
              <TableCell className="code text-2xs text-muted-foreground">
                {a.vigente_desde ? formatDate(a.vigente_desde) : "—"}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-1.5">
                  {a.puede_firmar ? (
                    <Badge variant="success-soft" estado>
                      firma
                    </Badge>
                  ) : null}
                  {a.puede_dar_accesos ? (
                    <Badge variant="brand-soft" estado>
                      {t("da accesos")}</Badge>
                  ) : null}
                  <Button
                    size="iconSm"
                    variant="ghost"
                    aria-label={`Quitar el acceso de ${a.email}`}
                    disabled={quitar.isPending}
                    onClick={() => quitar.mutate(a.id)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </WorkPanel>
  )
}

function Invitar({ roles, nota }: { roles: RolDisponible[]; nota?: string }) {
  const [email, setEmail] = React.useState("")
  const [rol, setRol] = React.useState("")
  const [enlace, setEnlace] = React.useState<string | null>(null)
  const { toast } = useToast()
  const qc = useQueryClient()

  const concedibles = roles.filter((r) => r.concedible_por_ti)

  const crear = useMutation({
    mutationFn: () =>
      invitar({ email, rol, motivo: "Alta de un miembro del equipo." }),
    onSuccess: (r) => {
      // El token se devuelve UNA vez. Si esta pantalla no lo muestra ahora, no
      // hay forma de recuperarlo: sólo se guarda su hash.
      setEnlace(`${window.location.origin}${r.ruta_para_aceptar}`)
      setEmail("")
      setRol("")
      toast({ title: `Invitación creada para ${r.email}`, description: r.aviso })
      void qc.invalidateQueries({ queryKey: ["invitaciones"] })
    },
    onError: onWriteError("la invitación", "crear"),
  })

  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto)
      toast({ title: "Enlace copiado" })
    } catch {
      toast({
        variant: "warning",
        title: "No se pudo copiar",
        description: "Selecciónalo y cópialo a mano.",
      })
    }
  }

  const listo = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email) && rol !== ""

  return (
    <WorkPanel title={t("Invitar a alguien")} footnote={nota} bodyClassName="p-2.5">
      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_auto] md:items-end">
        <div className="space-y-1">
          <Label htmlFor="inv-email">{t("Correo")}</Label>
          <Input
            id="inv-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("persona@empresa.com")}
            className="text-xs"
            aria-invalid={email !== "" && !listo && rol !== ""}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-rol">{t("Rol")}</Label>
          <select
            id="inv-rol"
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{t("Elige el rol…")}</option>
            {concedibles.map((r) => (
              <option key={r.codigo} value={r.codigo}>
                {r.codigo}
                {r.puede_firmar ? " · firma cierres" : ""}
              </option>
            ))}
          </select>
        </div>
        <Button size="sm" disabled={!listo || crear.isPending} onClick={() => crear.mutate()}>
          <UserPlus />
          {crear.isPending ? "Creando…" : "Crear invitación"}
        </Button>
      </div>

      {rol ? (
        <p className="mt-1.5 text-2xs leading-5 text-muted-foreground">
          <span className="code">{rol}</span> {" "}{t("podrá:")}{" "}
          {roles.find((r) => r.codigo === rol)?.capacidades.join(", ")}.
        </p>
      ) : null}

      {concedibles.length < roles.length ? (
        <p className="mt-1.5 text-2xs leading-5 text-muted-foreground">
          {t("No puedes conceder")}{" "}
          <span className="code">
            {roles
              .filter((r) => !r.concedible_por_ti)
              .map((r) => r.codigo)
              .join(", ")}
          </span>
          {t(". No es un límite arbitrario: si pudieras conceder un rol que firma, podrías dártelo a ti y la separación de funciones del cierre dejaría de significar nada.")}</p>
      ) : null}

      {enlace ? (
        <div className="mt-2.5 space-y-1.5 rounded-md border border-brand/30 bg-brand-soft/40 px-2.5 py-2">
          <p className="flex items-center gap-1.5 text-2xs font-medium">
            <Mail className="h-3 w-3" />
            {t("El sistema NO envía correos: copia este enlace y hazlo llegar tú.")}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <code className="min-w-0 flex-1 truncate rounded bg-surface px-1.5 py-1 text-2xs">
              {enlace}
            </code>
            <Button size="xs" variant="outline" onClick={() => void copiar(enlace)}>
              <Copy />
              {t("Copiar")}</Button>
          </div>
          <p className="text-2xs leading-4 text-muted-foreground">
            {t("Se muestra una sola vez: del enlace sólo se guarda su huella, nunca el enlace. Si lo pierdes, revoca la invitación y crea otra.")}</p>
        </div>
      ) : null}
    </WorkPanel>
  )
}

function Invitaciones() {
  const q = useQuery({ queryKey: ["invitaciones"], queryFn: fetchInvitaciones })
  const qc = useQueryClient()

  const revocar = useMutation({
    mutationFn: revocarInvitacion,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["invitaciones"] }),
    onError: onWriteError("la invitación", "revocar"),
  })

  return (
    <WorkPanel
      title={t("Invitaciones")}
      footnote={t("Una invitación vale 14 días, es de un solo uso y sólo funciona para el correo al que se dirigió: si se filtra, no le sirve a nadie más.")}
    >
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[18rem]">{t("Correo")}</TableHead>
            <TableHead className="w-28">{t("Rol")}</TableHead>
            <TableHead className="w-28">{t("Estado")}</TableHead>
            <TableHead className="w-32">{t("Vence")}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {(q.data ?? []).map((i) => (
            <TableRow key={i.id}>
              <TableCell className="truncate" title={i.email}>
                {i.email}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="code">
                  {i.rol}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge
                  variant={
                    i.estado === "aceptada"
                      ? "success-soft"
                      : i.estado === "pendiente"
                        ? "warning-soft"
                        : "outline"
                  }
                  estado
                >
                  {i.estado}
                </Badge>
              </TableCell>
              <TableCell className="code text-2xs text-muted-foreground">
                {formatDate(i.expira_en)}
              </TableCell>
              <TableCell className="text-right">
                {i.estado === "pendiente" ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={revocar.isPending}
                    onClick={() => revocar.mutate(i.id)}
                  >
                    <Link2 />
                    {t("Revocar")}</Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </WorkPanel>
  )
}
